import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import Dropdown from "react-bootstrap/Dropdown";
import {
  IoClose,
  IoGridOutline,
  IoHomeOutline,
  IoListOutline,
  IoRadioOutline,
  IoSearch,
  IoTicketOutline,
  IoTrendingUp,
} from "react-icons/io5";
import { IoIosArrowForward } from "react-icons/io";
import WebsiteBrandLogo from "../common/WebsiteBrandLogo";
import CoinSwitcher from "../common-components/CoinSwitcher";
import Sidebar from "../pages/dashboard/Sidebar";
import SportIcon from "../common/SportIcon";
import { logout } from "../actions/logout";
import { getGamedatas, getPhoneNavCounts, getTrendingLeagues, getWalletDetails } from "../actions/gameSettings";
import { SELECT_SPORT } from "../constants";
import { Images } from "../../utility/image";
import userlogo from "../../asset/img/user_logo.png";
import { WALLET_REFRESH_EVENT } from "../../utility/walletRefresh";
import { isDTS } from "../../utility/platform";
import { getTerm } from "../../utility/complianceCopy";
import { useComplianceConfig } from "../../utility/useComplianceConfig";
import { LIVE_SECTION_LABEL } from "../../utility/siteCopy";
import { formatMoney } from "../lib/wagerFinancials";
import { COIN_MODES, getCoinModeBalance, useCoinMode } from "../../context/CoinModeContext";
import { getGoldCoinName, getSweepsCoinName } from "../../utility/complianceCopy";
import { usePhoneShell } from "../../context/PhoneShellContext";
import { formatBadgeCount, slipSelectionCount } from "../../utility/phoneShell";
import isLogin from "../lib/isLogin";
import {
  ROUTES,
  allSportsPath,
  boardSportFromPath,
  isRoutePath,
  liveBoardPath,
  sportBoardPath,
  ALL_SPORTS_KEY,
  LIVE_BOARD_KEY,
} from "../../constants/routes";
import "./phoneShell.css";

const EMPTY_COUNTS = { liveEvents: 0, activeEntries: 0 };

export default function PhoneChrome() {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { coinMode } = useCoinMode();
  const accountData = useSelector((state) => state.account);
  const slip = useSelector((state) => state.checkIndReducer?.checkInd);
  const {
    boardQuery,
    setBoardQuery,
    searchOpen,
    setSearchOpen,
    allSportsOpen,
    setAllSportsOpen,
    entryOpen,
    setEntryOpen,
    closeSheets,
  } = usePhoneShell();

  useComplianceConfig();

  const [wallet, setWallet] = useState({});
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [sports, setSports] = useState([]);
  const [trending, setTrending] = useState([]);
  const [trendingOpen, setTrendingOpen] = useState(true);
  const [sportQuery, setSportQuery] = useState("");

  const email = accountData?.emailId;
  const loggedIn = Boolean(email && isLogin());

  useEffect(() => {
    if (location.state?.openEntrySheet) setEntryOpen(true);
  }, [location.state, setEntryOpen]);

  useEffect(() => {
    let cancelled = false;
    const loadWallet = async () => {
      if (!loggedIn || !email) {
        setWallet({});
        return;
      }
      try {
        const { status, result } = await getWalletDetails({ email });
        if (!cancelled && status && result?.walletData) setWallet(result.walletData);
      } catch {
        if (!cancelled) setWallet({});
      }
    };
    loadWallet();
    window.addEventListener(WALLET_REFRESH_EVENT, loadWallet);
    return () => {
      cancelled = true;
      window.removeEventListener(WALLET_REFRESH_EVENT, loadWallet);
    };
  }, [email, loggedIn]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [sportRes, trendRes, countRes] = await Promise.all([
        getGamedatas(dispatch).catch(() => null),
        getTrendingLeagues().catch(() => null),
        // Counts need a session; guests keep empty badges.
        loggedIn ? getPhoneNavCounts().catch(() => null) : Promise.resolve(null),
      ]);
      if (cancelled) return;
      setSports(Array.isArray(sportRes?.result) ? sportRes.result : []);
      setTrending(Array.isArray(trendRes?.result) ? trendRes.result : []);
      setCounts(countRes?.result || EMPTY_COUNTS);
    };
    load();
    const timer = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [dispatch, location.pathname, loggedIn]);

  const boardSport = boardSportFromPath(location.pathname);
  const onBoard = boardSport !== null;
  const sportsRef = useRef(null);

  useEffect(() => {
    const active = sportsRef.current?.querySelector(".phone-sports__chip.is-active");
    if (!active || typeof active.scrollIntoView !== "function") return;
    active.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [boardSport, sports.length]);

  const requireLogin = () => {
    closeSheets();
    history.push("/login");
  };

  const goSport = (sportKey) => {
    dispatch({ type: SELECT_SPORT, data: sportKey });
    closeSheets();
    setBoardQuery("");
    setSearchOpen(false);
    history.push({
      pathname: sportKey === LIVE_BOARD_KEY ? liveBoardPath() : sportBoardPath(sportKey),
      state: { selectedSport: sportKey },
    });
  };

  const openEntry = () => {
    if (!loggedIn) {
      requireLogin();
      return;
    }
    if (entryOpen) {
      setEntryOpen(false);
      return;
    }
    setAllSportsOpen(false);
    setEntryOpen(true);
    if (!onBoard && !isRoutePath(location.pathname, "openBet")) {
      history.push({ pathname: allSportsPath(), state: { openEntrySheet: true, selectedSport: ALL_SPORTS_KEY } });
    }
  };

  const onSearchChange = (value) => {
    setBoardQuery(value);
    // One history entry when search starts off the board. Later keystrokes stay put.
    if (!onBoard && location.pathname !== allSportsPath()) {
      history.push({ pathname: allSportsPath(), state: { selectedSport: ALL_SPORTS_KEY } });
    }
  };

  const toggleTeamSearch = () => {
    setSearchOpen((open) => {
      if (open) setBoardQuery("");
      return !open;
    });
  };

  const openTrending = (league) => {
    if (!league?.group || !league?.key) return;
    dispatch({ type: SELECT_SPORT, data: league.group });
    closeSheets();
    history.push({
      pathname: sportBoardPath(league.group),
      state: { selectedSport: league.group, selectedLeagueKey: league.key },
    });
  };

  const activeBalance = isDTS()
    ? formatMoney(getCoinModeBalance(wallet, coinMode))
    : formatMoney(wallet?.userBalance);
  const activeCoinName = coinMode === COIN_MODES.DREAM ? getSweepsCoinName() : getGoldCoinName();
  const slipCount = formatBadgeCount(slipSelectionCount(slip));
  const liveCount = formatBadgeCount(counts.liveEvents);
  const entryCount = formatBadgeCount(counts.activeEntries);

  const tabs = [
    {
      id: "home",
      label: "Home",
      icon: IoHomeOutline,
      active: boardSport === ALL_SPORTS_KEY && !allSportsOpen && !entryOpen,
      onClick: () => {
        closeSheets();
        goSport(ALL_SPORTS_KEY);
      },
    },
    {
      id: "sports",
      label: "All Sports",
      icon: IoGridOutline,
      active: allSportsOpen,
      onClick: () => {
        setEntryOpen(false);
        setSportQuery("");
        if (!onBoard) {
          history.push({ pathname: allSportsPath(), state: { selectedSport: ALL_SPORTS_KEY } });
        }
        setAllSportsOpen((open) => !open);
      },
    },
    {
      id: "live",
      label: LIVE_SECTION_LABEL,
      icon: IoRadioOutline,
      badge: liveCount,
      active: boardSport === LIVE_BOARD_KEY && !entryOpen && !allSportsOpen,
      onClick: () => {
        closeSheets();
        goSport(LIVE_BOARD_KEY);
      },
    },
    {
      id: "active",
      label: isDTS() ? `Active ${getTerm("entry", { plural: true })}` : "Active Bets",
      icon: IoListOutline,
      badge: entryCount,
      active: isRoutePath(location.pathname, "openBet") && !entryOpen && !allSportsOpen,
      onClick: () => {
        if (!loggedIn) {
          requireLogin();
          return;
        }
        closeSheets();
        history.push(ROUTES.openBet);
      },
    },
    {
      id: "builder",
      label: isDTS() ? "Entry Builder" : "Bet Builder",
      icon: IoTicketOutline,
      badge: slipCount,
      active: entryOpen,
      onClick: openEntry,
    },
  ];

  return (
    <>
      <header className={`phone-top${searchOpen ? " phone-top--search" : ""}`}>
        <div className="phone-top__row">
          <Link to={allSportsPath()} className="phone-top__logo" aria-label="Home">
            <WebsiteBrandLogo asLink={false} />
          </Link>
          <button
            type="button"
            className="phone-icon-btn"
            aria-label={searchOpen ? "Close search" : "Search teams"}
            aria-expanded={searchOpen}
            onClick={toggleTeamSearch}
          >
            {searchOpen ? <IoClose /> : <IoSearch />}
          </button>
          {loggedIn ? (
            <>
              <Dropdown className="phone-balance" align="end">
                <Dropdown.Toggle variant="dark" id="phone-balance" aria-label="Account balance">
                  <span>{isDTS() ? activeCoinName : "Balance"}</span>
                  <strong>{activeBalance}</strong>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {isDTS() ? (
                    <div className="phone-balance__switcher">
                      <CoinSwitcher wallet={wallet} />
                    </div>
                  ) : (
                    <>
                      <Dropdown.ItemText>Balance: {formatMoney(wallet?.userBalance)}</Dropdown.ItemText>
                      <Dropdown.ItemText>At risk: {formatMoney(wallet?.riskAmount)}</Dropdown.ItemText>
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>
              <AccountMenu history={history} dispatch={dispatch} />
            </>
          ) : (
            <div className="phone-auth">
              <Link to="/login" className="img_btn img_btn_fw loginBtn phone-auth__btn">
                Login
              </Link>
              <Link to="/signup" className="img_grd_btn img_btn_fw loginBtn phone-auth__btn">
                Sign Up
              </Link>
            </div>
          )}
        </div>
        {searchOpen && (
          <form
            className="phone-search"
            onSubmit={(event) => {
              event.preventDefault();
              onSearchChange(boardQuery);
            }}
          >
            <input
              type="search"
              value={boardQuery}
              placeholder="Search teams"
              aria-label="Search teams"
              onChange={(event) => onSearchChange(event.target.value)}
              autoFocus
            />
          </form>
        )}
        <nav className="phone-sports" aria-label="Sports" ref={sportsRef}>
          {sports.map((sport) => {
            const name = sport._id || sport.name;
            if (!name) return null;
            return (
              <button
                key={name}
                type="button"
                className={`phone-sports__chip${boardSport === name ? " is-active" : ""}`}
                onClick={() => goSport(name)}
              >
                <SportIcon sportName={name} iconUrl={sport.iconUrl} size={18} alt="" />
                <span>{name}</span>
              </button>
            );
          })}
        </nav>
      </header>

      {allSportsOpen && (
        <div className="phone-drawer" role="dialog" aria-label="All Sports">
          <button type="button" className="phone-sheet-backdrop" aria-label="Close All Sports" onClick={() => setAllSportsOpen(false)} />
          <div className="phone-drawer__panel">
            <div className="phone-drawer__head">
              <h2>All Sports</h2>
              <button type="button" className="phone-icon-btn" aria-label="Close" onClick={() => setAllSportsOpen(false)}>
                <IoClose />
              </button>
            </div>
            <input
              type="search"
              className="phone-drawer__search"
              placeholder="Search sports"
              aria-label="Search sports"
              value={sportQuery}
              onChange={(event) => setSportQuery(event.target.value)}
            />
            {trending.length > 0 && !sportQuery.trim() ? (
              <div className="mg-sidebar-component phone-trending">
                <ul>
                  <li>
                    <div
                      className={`sidebarListHeader${trendingOpen ? " isExpand" : ""}`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={trendingOpen}
                      aria-controls="phone-trending-list"
                      onClick={() => setTrendingOpen((open) => !open)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setTrendingOpen((open) => !open);
                        }
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span className="phone-trending__icon" aria-hidden="true">
                          <IoTrendingUp />
                        </span>
                        <h3 className="fs15">Trending</h3>
                      </div>
                      <button type="button" className="arrowIcon" tabIndex={-1} aria-hidden="true">
                        <IoIosArrowForward />
                      </button>
                    </div>
                    {trendingOpen ? (
                      <div className="sidebarListBody" id="phone-trending-list">
                        <ul>
                          {trending.map((league) => (
                            <li
                              key={league.key}
                              className="sidebarListHeader"
                              role="button"
                              tabIndex={0}
                              onClick={() => openTrending(league)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  openTrending(league);
                                }
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <SportIcon
                                  sportName={league.group || league.title}
                                  size={22}
                                  alt=""
                                />
                                <h3 className="fs14">{league.title}</h3>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </li>
                </ul>
              </div>
            ) : null}
            <Sidebar sportFilter={sportQuery} onNavigate={() => setAllSportsOpen(false)} />
          </div>
        </div>
      )}

      {entryOpen && (
        <button type="button" className="phone-sheet-backdrop" aria-label="Close entry builder" onClick={() => setEntryOpen(false)} />
      )}

      <nav className="phone-tabs" aria-label="Primary">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              className={tab.active ? "is-active" : ""}
              onClick={tab.onClick}
            >
              <span className="phone-tabs__icon">
                <Icon aria-hidden="true" />
                {tab.badge ? <span className="phone-tabs__badge">{tab.badge}</span> : null}
              </span>
              <span className="phone-tabs__label">{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}

function AccountMenu({ history, dispatch }) {
  return (
    <Dropdown className="phone-account" align="end">
      <Dropdown.Toggle variant="dark" id="phone-account" aria-label="Account">
        <img
          src={Images.userIcon || userlogo}
          alt=""
          className="img-fluid user_logo"
        />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => history.push("/profile")}>Profile</Dropdown.Item>
        <Dropdown.Item onClick={() => history.push("/changePassword")}>Change Password</Dropdown.Item>
        <Dropdown.Item onClick={() => history.push("/wallet")}>Wallet</Dropdown.Item>
        {isDTS() && (
          <>
            <Dropdown.Item onClick={() => history.push("/redeem")}>Prize Request</Dropdown.Item>
            <Dropdown.Item onClick={() => history.push("/kyc")}>Verify Identity</Dropdown.Item>
            <Dropdown.Item onClick={() => history.push("/amoe")}>Free Entry (AMOE)</Dropdown.Item>
          </>
        )}
        <Dropdown.Item onClick={() => history.push("/twofactorauth")}>2FA</Dropdown.Item>
        <Dropdown.Item onClick={() => history.push("/support_ticket")}>Support</Dropdown.Item>
        <Dropdown.Item onClick={() => logout(history, dispatch)}>Sign Out</Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}
